import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, ChevronRight } from 'lucide-react';
import { searchItems } from '../services/api';
import './Search.css';

const SearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    
    try {
      const data = await searchItems(searchQuery);
      // 确保 data 是数组
      if (Array.isArray(data)) {
        setResults(data);
      } else if (data && Array.isArray(data.results)) {
        setResults(data.results);
      } else {
        console.error('Search returned non-array data:', data);
        setResults([]);
      }
      setError(null);
    } catch (error) {
      console.error('Error searching:', error);
      setResults([]);
      setError('搜索出错，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [query, performSearch]);

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
  };

  // 确保 results 是数组
  const safeResults = Array.isArray(results) ? results : [];

  // 高亮匹配文本
  const highlightText = (text, query) => {
    if (!query.trim()) return text;
    
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="highlight">{part}</mark>
      ) : (
        <span key={index}>{part}</span>
      )
    );
  };

  return (
    <div className="search-page">
      <div className="container">
        {/* Search Header */}
        <div className="search-header">
          <h1 className="search-title">搜索学训</h1>
          <p className="search-subtitle">输入关键词搜索学训相关内容</p>
          
          {/* Search Input */}
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <input
              type="text"
              className="search-input"
              placeholder="输入关键词搜索..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button className="clear-btn" onClick={handleClear}>
                <X size={18} />
              </button>
            )}
          </div>


        </div>

        {/* Search Results */}
        <div className="search-results">
          {loading ? (
            <div className="search-loading">
              <div className="loading-spinner"></div>
              <p>搜索中...</p>
            </div>
          ) : hasSearched ? (
            <>
                <div className="results-header">
                  <span className="results-count">
                    找到 <strong>{safeResults.length}</strong> 条结果
                  </span>
                </div>

                {safeResults.length > 0 ? (
                  <div className="results-list">
                    {safeResults.map((result, index) => (
                      <Link
                        key={result.number}
                        to={`/item/${result.number}`}
                        className="result-card animate-fade-in"
                        style={{ animationDelay: `${index * 0.05}s` }}
                      >
                        <div className="result-header">
                          <span className="result-number">第 {result.number} 条</span>
                          <div className="result-breadcrumb">
                            <span>{result.partName}</span>
                            <ChevronRight size={14} />
                            <span>{result.chapterTitle}</span>
                            {result.subsectionTitle && (
                              <>
                                <ChevronRight size={14} />
                                <span>{result.subsectionTitle}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="result-content">
                          {highlightText(result.content, query)}
                        </p>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="no-results">
                    <Search size={64} className="no-results-icon" />
                    <h3>未找到相关结果</h3>
                    <p>请尝试其他关键词</p>
                  </div>
                )}
              </>
          ) : (
            <div className="search-tips">
              {/* Hot Keywords */}
              <div className="hot-keywords">
                <h4>热门搜索</h4>
                <div className="keywords-list">
                  {['品德', '勤奋', '学习', '做人', '成功', '坚持', '创新', '责任'].map((keyword) => (
                    <button
                      key={keyword}
                      className="keyword-btn"
                      onClick={() => setQuery(keyword)}
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              </div>
              <div className="tips-grid">
                <div className="tip-card">
                  <span className="tip-icon">🔍</span>
                  <h4>关键词搜索</h4>
                  <p>输入任意关键词，如"德"、"勤奋"、"学习"等</p>
                </div>
                <div className="tip-card">
                  <span className="tip-icon">📚</span>
                  <h4>全文检索</h4>
                  <p>搜索范围覆盖全部100条内容</p>
                </div>
                <div className="tip-card">
                  <span className="tip-icon">⚡</span>
                  <h4>实时结果</h4>
                  <p>输入时自动搜索，无需按回车</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchPage;
